import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { addDoc, collection, deleteDoc, getDocs, updateDoc, doc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { getCloudFunctionUrl } from '../config';
import {
  BUILTIN_ORDER_EMAIL_TEMPLATES,
  interpolateOrderEmail
} from '../utils/orderEmailTemplates';
import './OrderCustomerEmailModal.css';

const fetchWithTimeout = (url, options = {}, timeoutMs = 60000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(id));
};

const OrderCustomerEmailModal = ({ item, source, onClose, showMessage, onSent }) => {
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(BUILTIN_ORDER_EMAIL_TEMPLATES[0].id);
  const [toEmail, setToEmail] = useState(String(item?.email || '').trim());
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const [deletingTpl, setDeletingTpl] = useState(false);
  const [error, setError] = useState('');

  const allTemplates = useMemo(
    () => [...BUILTIN_ORDER_EMAIL_TEMPLATES, ...savedTemplates],
    [savedTemplates]
  );

  const applyTemplate = useCallback((tpl) => {
    if (!tpl) return;
    setSelectedId(tpl.id);
    setSubject(interpolateOrderEmail(tpl.subject || '', item));
    setBody(interpolateOrderEmail(tpl.body || '', item));
  }, [item]);

  const loadSavedTemplates = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'orderEmailTemplates'));
      const list = snap.docs.map((d) => ({
        id: d.id,
        name: d.data().name || 'Plantilla',
        subject: d.data().subject || '',
        body: d.data().body || ''
      }));
      setSavedTemplates(list);
    } catch (e) {
      console.error('Error cargando plantillas de pedido:', e);
    }
  }, []);

  useEffect(() => {
    loadSavedTemplates();
    applyTemplate(BUILTIN_ORDER_EMAIL_TEMPLATES[0]);
    // Solo al abrir el modal: no pisar el texto si el pedido se actualiza en vivo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orderLabel = item?.orderNumber || (item?.id ? item.id.slice(0, 6) : '');
  const history = Array.isArray(item?.adminCustomerEmails) ? item.adminCustomerEmails : [];

  const handleSend = async () => {
    const to = toEmail.trim();
    const subj = subject.trim();
    const text = body.trim();
    if (!to || !to.includes('@')) {
      setError('Ingresá un email válido.');
      return;
    }
    if (!subj || !text) {
      setError('Completá asunto y mensaje.');
      return;
    }
    let url = '';
    try {
      url = getCloudFunctionUrl('sendOrderCustomerEmail');
    } catch {
      url = '';
    }
    if (!url) {
      setError('No hay URL configurada para enviar emails.');
      showMessage('No hay URL configurada para enviar emails.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Sesión inválida. Volvé a iniciar sesión.');
      }
      const selected = allTemplates.find((t) => t.id === selectedId);
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          collectionName: source,
          orderId: item.id,
          to,
          subject: subj,
          messageText: text,
          templateName: selected?.name || ''
        })
      }, 60000);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data?.message || data?.error || `Error HTTP ${response.status}`);
      }
      showMessage(`Email enviado a ${to}`);
      if (typeof onSent === 'function') onSent();
      onClose();
    } catch (e) {
      console.error('Error enviando email de pedido:', e);
      const msg = e.name === 'AbortError' ? 'El envío tardó demasiado.' : (e.message || 'Error enviando el email');
      setError(msg);
      showMessage('Error enviando el email.');
    } finally {
      setSending(false);
    }
  };

  const handleSaveTemplate = async () => {
    const name = window.prompt('Nombre de la plantilla (se guarda el texto con {{placeholders}} si los dejás):');
    if (!name || !name.trim()) return;
    if (!subject.trim() || !body.trim()) {
      setError('Completá asunto y mensaje para guardar la plantilla.');
      return;
    }
    setSavingTpl(true);
    setError('');
    try {
      const payload = {
        name: name.trim().slice(0, 80),
        subject: subject.trim(),
        body: body.trim(),
        updatedAt: new Date()
      };
      if (selectedId && !String(selectedId).startsWith('builtin:')) {
        await updateDoc(doc(db, 'orderEmailTemplates', selectedId), payload);
        showMessage('Plantilla actualizada.');
      } else {
        const ref = await addDoc(collection(db, 'orderEmailTemplates'), {
          ...payload,
          createdAt: new Date()
        });
        setSelectedId(ref.id);
        showMessage('Plantilla guardada.');
      }
      await loadSavedTemplates();
    } catch (e) {
      console.error('Error guardando plantilla de pedido:', e);
      setError(e.message || 'No se pudo guardar la plantilla.');
      showMessage('Error guardando plantilla');
    } finally {
      setSavingTpl(false);
    }
  };

  const isBuiltinSelected = String(selectedId || '').startsWith('builtin:');

  const handleClose = () => {
    if (sending || savingTpl || deletingTpl) return;
    onClose();
  };

  const handleDeleteTemplate = async (tpl) => {
    if (!tpl?.id || String(tpl.id).startsWith('builtin:')) return;
    const confirmed = window.confirm(`¿Eliminar la plantilla “${tpl.name}”? Esta acción no se puede deshacer.`);
    if (!confirmed) return;
    setDeletingTpl(true);
    setError('');
    try {
      await deleteDoc(doc(db, 'orderEmailTemplates', tpl.id));
      const remaining = savedTemplates.filter((t) => t.id !== tpl.id);
      setSavedTemplates(remaining);
      if (selectedId === tpl.id) {
        applyTemplate(BUILTIN_ORDER_EMAIL_TEMPLATES[0]);
      }
      showMessage('Plantilla eliminada.');
    } catch (e) {
      console.error('Error eliminando plantilla de pedido:', e);
      setError(e.message || 'No se pudo eliminar la plantilla.');
      showMessage('Error eliminando plantilla');
    } finally {
      setDeletingTpl(false);
    }
  };

  return createPortal(
    <div className="oce-modal-overlay" role="presentation">
      <div
        className="oce-modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="oce-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="oce-modal-header">
          <div>
            <h3 id="oce-modal-title">Email al cliente {orderLabel ? `· #${orderLabel}` : ''}</h3>
            <p className="oce-modal-subtitle">
              Se envía con la plantilla Priball. Elegí un mensaje estándar o editá el texto.
            </p>
          </div>
          <button type="button" className="notes-modal-close" onClick={handleClose} aria-label="Cerrar" disabled={sending || savingTpl || deletingTpl}>✕</button>
        </div>

        <div className="oce-chips" role="list">
          {allTemplates.map((tpl) => {
            const isBuiltin = String(tpl.id).startsWith('builtin:');
            return (
              <span
                key={tpl.id}
                className={`oce-chip-wrap ${selectedId === tpl.id ? 'is-active' : ''}`}
                role="listitem"
              >
                <button
                  type="button"
                  className={`oce-chip ${selectedId === tpl.id ? 'is-active' : ''}`}
                  onClick={() => applyTemplate(tpl)}
                  disabled={sending || deletingTpl}
                >
                  {tpl.name}
                </button>
                {!isBuiltin && (
                  <button
                    type="button"
                    className="oce-chip-delete"
                    title={`Eliminar “${tpl.name}”`}
                    aria-label={`Eliminar plantilla ${tpl.name}`}
                    onClick={() => handleDeleteTemplate(tpl)}
                    disabled={sending || savingTpl || deletingTpl}
                  >
                    ×
                  </button>
                )}
              </span>
            );
          })}
        </div>

        <div className="oce-meta-row">
          <label className="oce-field">
            Destinatario
            <input
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              disabled={sending}
            />
          </label>

          <label className="oce-field">
            Asunto
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending}
              maxLength={180}
            />
          </label>
        </div>

        <label className="oce-field oce-field-message">
          Mensaje
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={sending}
            rows={14}
          />
          <p className="oce-hint">
            Placeholders: {'{{nombre}}'} {'{{orderNumber}}'} {'{{total}}'} {'{{trackingNumber}}'} {'{{trackingLink}}'}
          </p>
        </label>

        {error ? <p className="oce-error">{error}</p> : null}

        {history.length > 0 && (
          <div className="oce-history">
            <h4>Enviados antes</h4>
            <ul>
              {history.slice(-5).reverse().map((row, idx) => {
                const when = row.sentAt?.toDate?.()
                  ? row.sentAt.toDate()
                  : (row.sentAt?.seconds ? new Date(row.sentAt.seconds * 1000) : null);
                return (
                  <li key={`${row.subject || 'mail'}-${idx}`}>
                    {when ? `${when.toLocaleString('es-AR')} · ` : ''}
                    {row.subject || '(sin asunto)'}
                    {row.to ? ` → ${row.to}` : ''}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="oce-actions">
          <button type="button" className="btn btn-outline" onClick={handleClose} disabled={sending || savingTpl || deletingTpl}>
            Cancelar
          </button>
          {!isBuiltinSelected && (
            <button
              type="button"
              className="btn btn-outline oce-delete-tpl-btn"
              onClick={() => {
                const tpl = savedTemplates.find((t) => t.id === selectedId);
                if (tpl) handleDeleteTemplate(tpl);
              }}
              disabled={sending || savingTpl || deletingTpl}
            >
              {deletingTpl ? 'Eliminando…' : 'Eliminar plantilla'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleSaveTemplate}
            disabled={sending || savingTpl || deletingTpl}
          >
            {savingTpl ? 'Guardando…' : 'Guardar plantilla'}
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={handleSend}
            disabled={sending || deletingTpl}
          >
            {sending ? 'Enviando…' : 'Enviar email'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OrderCustomerEmailModal;
